import React, { FC, useEffect, useState } from "react";
import BoaEmbed from "./boaEmbed";

interface PersistentBoaEmbedProps {
  /**
   * Whether this embed should be loaded at all.
   *
   * Controls MOUNTING: once true the frame is created and never destroyed, so
   * the embedded app starts loading from this moment. Deliberately not tied to
   * "is my tab showing" — a caller that wants the panel warm before it is
   * looked at sets this early and holds `visible` back.
   */
  active: boolean;
  /**
   * Whether the loaded frame should be shown. Defaults to `active`.
   *
   * Split from `active` so the frame can load while still hidden — which is
   * what lets the Documents tab preload its panel from another tab entirely,
   * and what keeps that panel from appearing above the stipulations while
   * those are still a spinner.
   */
  visible?: boolean;
  /** Path within the boa app. */
  path: string;
  title: string;
  initialHeight?: number;
  /**
   * Identity of the thing being displayed — normally the application id.
   *
   * Changing it tears the frame down and starts over, which is the one case
   * that SHOULD refetch.
   *
   * It is not the only thing that can, though: `path` lands on the iframe's
   * `src`, so changing it navigates the live frame and reboots boa exactly as
   * an unmount would. `path` must therefore be stable for a given
   * `instanceKey` — derive both from the same identity, as the Documents tab
   * does, or the persistence this component exists for is silently lost.
   */
  instanceKey: string;
  onDataChanged?: () => void;
}

/**
 * A boa embed that loads on first view and then stays loaded.
 *
 * `BoaEmbed` inside a `<Route>` is unmounted the moment the user picks another
 * tab, which destroys the iframe. Coming back boots the entire boa Next.js app
 * again — framework, chunks, session read, API round trip — so a reviewer
 * comparing the disclosures against the activity log pays that cost on every
 * switch, and watches a spinner each time for data that has not changed.
 *
 * Two behaviours, and the tension between them is the whole point:
 *
 *   - **Deferred**: nothing is mounted until the caller says so. Whether that
 *     is "the tab was opened" or "this page has something worth preloading" is
 *     the caller's call — but an iframe booting a second SPA is never free, so
 *     something has to decide, and it is never simply "always".
 *   - **Sticky**: once mounted it is never unmounted, only hidden. Hiding an
 *     iframe with `display: none` keeps its document alive — the panel is
 *     exactly as the reviewer left it, scroll position and expanded audit
 *     trails included.
 *
 * `instanceKey` is the escape hatch: a different application is a different
 * record, and its panel must not be inherited from the last one.
 *
 * Deliberately not solved by caching in Redux instead. The data lives behind an
 * iframe boundary, so the host cannot hold it — and pulling the disclosure
 * record into the portal's store to avoid a refetch would recreate exactly the
 * duplication this feature moved to the API to escape.
 */
const PersistentBoaEmbed: FC<PersistentBoaEmbedProps> = ({
  active,
  visible = active,
  path,
  title,
  initialHeight,
  instanceKey,
  onDataChanged,
}) => {
  /**
   * Which instance has been opened at least once — the latch that makes this
   * lazy on the way in and sticky thereafter.
   *
   * State rather than a ref, and compared against `instanceKey` rather than
   * cleared by an effect. A ref mutated during render cannot re-render on its
   * own, so switching to another application would render one frame with the
   * latch still set and mount that application's panel unbidden, before the
   * cleanup ran. Here a new key simply stops matching, so nothing is mounted
   * until the tab is opened again.
   */
  const [openedInstance, setOpenedInstance] = useState<string | null>(null);

  useEffect(() => {
    if (active && openedInstance !== instanceKey) {
      setOpenedInstance(instanceKey);
    }
  }, [active, instanceKey, openedInstance]);

  if (openedInstance !== instanceKey) {
    return null;
  }

  return (
    // `display: none` rather than conditional rendering: unmounting is what we
    // are avoiding. The frame keeps running while hidden and costs nothing —
    // boa reports its height through a ResizeObserver, and a hidden element
    // measures 0, which BoaEmbed already ignores as a non-positive height.
    <div style={{ display: visible ? "block" : "none" }}>
      <BoaEmbed
        key={instanceKey}
        path={path}
        title={title}
        initialHeight={initialHeight}
        onDataChanged={onDataChanged}
      />
    </div>
  );
};

export default PersistentBoaEmbed;
